// Sentry Error Tracking Configuration
// Created: February 5, 2026
// Enhancement 5.3 - Error Tracking Setup

import * as Sentry from '@sentry/react';
import { BrowserTracing } from '@sentry/react';

export interface SentryConfig {
  dsn: string;
  environment: string;
  release?: string;
  tracesSampleRate?: number;
  replaysSessionSampleRate?: number;
  replaysOnErrorSampleRate?: number;
}

/**
 * Initialize Sentry error tracking
 */
export function initSentry(config?: Partial<SentryConfig>) {
  const dsn = import.meta.env.VITE_SENTRY_DSN;
  const environment = import.meta.env.VITE_SENTRY_ENVIRONMENT || import.meta.env.MODE;
  const release = import.meta.env.VITE_APP_VERSION || 'unknown';

  // Skip initialization if DSN not configured
  if (!dsn) {
    console.warn('Sentry DSN not configured. Error tracking disabled.');
    return;
  }

  // Skip in development unless explicitly enabled
  if (environment === 'development' && !import.meta.env.VITE_SENTRY_ENABLED) {
    console.log('Sentry disabled in development mode');
    return;
  }

  Sentry.init({
    dsn,
    environment,
    release,
    
    // Performance Monitoring
    integrations: [
      new BrowserTracing({
        // Set sampling rate for performance monitoring
        tracePropagationTargets: [
          'localhost',
          /^https:\/\/.*\.supabase\.co/,
          /^https:\/\/nexqr\.online/,
        ],
      }),
      new Sentry.Replay({
        // Mask all text content for privacy
        maskAllText: true,
        blockAllMedia: true,
      }),
    ],

    // Performance monitoring sample rate (0.0 to 1.0)
    tracesSampleRate: config?.tracesSampleRate ?? 0.1, // 10% of transactions

    // Session Replay
    replaysSessionSampleRate: config?.replaysSessionSampleRate ?? 0.1, // 10% of sessions
    replaysOnErrorSampleRate: config?.replaysOnErrorSampleRate ?? 1.0, // 100% of errors

    // Error filtering
    beforeSend(event, hint) {
      // Filter out known non-critical errors
      const error = hint.originalException;
      
      if (error && typeof error === 'object' && 'message' in error) {
        const message = String(error.message).toLowerCase();
        
        // Ignore network errors that are expected
        if (message.includes('network request failed')) {
          return null;
        }
        
        // Ignore ResizeObserver errors (common browser quirk)
        if (message.includes('resizeobserver')) {
          return null;
        }
        
        // Ignore CAPTCHA widget errors (handled separately)
        if (message.includes('turnstile')) {
          return null;
        }
      }
      
      return event;
    },

    // Ignore specific errors
    ignoreErrors: [
      // Browser extensions
      'top.GLOBALS',
      'chrome-extension://',
      'moz-extension://',
      // Random plugins/extensions
      'fb_xd_fragment',
      // Network errors
      'NetworkError',
      'Failed to fetch',
      // User cancellations
      'AbortError',
      'The user aborted a request',
    ],

    // Deny URLs (don't track errors from these sources)
    denyUrls: [
      // Browser extensions
      /extensions\//i,
      /^chrome:\/\//i,
      /^moz-extension:\/\//i,
      // Third-party scripts
      /google-analytics\.com/i,
      /googletagmanager\.com/i,
    ],
  });

  console.log(`Sentry initialized for ${environment} environment`);
}

/**
 * Set user context for error tracking
 */
export function setSentryUser(user: {
  id: string;
  email?: string;
  username?: string;
  plan?: string;
}) {
  Sentry.setUser({
    id: user.id,
    email: user.email,
    username: user.username,
    plan: user.plan,
  });
}

/**
 * Clear user context (on logout)
 */
export function clearSentryUser() {
  Sentry.setUser(null);
}

/**
 * Add breadcrumb for debugging
 */
export function addBreadcrumb(
  message: string,
  category: string,
  level: 'debug' | 'info' | 'warning' | 'error' = 'info',
  data?: Record<string, unknown>
) {
  Sentry.addBreadcrumb({
    message,
    category,
    level,
    data,
    timestamp: Date.now() / 1000,
  });
}

/**
 * Capture exception manually
 */
export function captureException(
  error: Error,
  context?: {
    tags?: Record<string, string>;
    extra?: Record<string, unknown>;
    level?: 'fatal' | 'error' | 'warning' | 'info' | 'debug';
  }
) {
  Sentry.captureException(error, {
    tags: context?.tags,
    extra: context?.extra,
    level: context?.level,
  });
}

/**
 * Capture message manually
 */
export function captureMessage(
  message: string,
  level: 'fatal' | 'error' | 'warning' | 'info' | 'debug' = 'info',
  context?: {
    tags?: Record<string, string>;
    extra?: Record<string, unknown>;
  }
) {
  Sentry.captureMessage(message, {
    level,
    tags: context?.tags,
    extra: context?.extra,
  });
}

/**
 * Set custom context
 */
export function setSentryContext(
  name: string,
  context: Record<string, unknown>
) {
  Sentry.setContext(name, context);
}

/**
 * Set custom tag
 */
export function setSentryTag(key: string, value: string) {
  Sentry.setTag(key, value);
}

/**
 * Start a new transaction for performance monitoring
 */
export function startTransaction(name: string, op: string) {
  return Sentry.startTransaction({
    name,
    op,
  });
}

/**
 * Wrap component with Sentry error boundary
 */
export const SentryErrorBoundary = Sentry.ErrorBoundary;

/**
 * Profile component with Sentry profiler
 */
export const SentryProfiler = Sentry.Profiler;
